import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Plus, Trash2, AlertTriangle, History, Flag } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["Not started", "On track", "At risk", "Passed", "Missed"];
const OPEN_STATUSES = ["Not started", "On track", "At risk"];
const statusCls = (st) => st === "Passed" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : st === "Missed" ? "text-red-700 bg-red-50 border-red-200"
  : st === "At risk" ? "text-amber-700 bg-amber-50 border-amber-200"
  : st === "On track" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : "text-zinc-500 bg-zinc-50 border-zinc-200";
const isOverdue = (i) => i.target_date && OPEN_STATUSES.includes(i.status) && new Date(i.target_date) < new Date(new Date().toDateString());
const inputCls = "border border-zinc-300 rounded px-2 py-1 text-[12px] bg-white";

function GateRow({ item, onSave, onDelete, busy }) {
  const [expand, setExpand] = useState(false);
  const patch = (body) => onSave(item.id, body);
  const overdue = isOverdue(item);
  return (
    <div data-testid={`gate-row-${item.id}`} className={`px-3 py-2.5 ${overdue ? "bg-red-50/40" : ""}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <span className="text-[11px] text-zinc-400 font-mono w-20 pt-1 flex-none">{item.ref}</span>
        <div className="flex-1 min-w-[240px]">
          <button onClick={() => setExpand((v) => !v)} className="text-left" data-testid={`gate-title-${item.id}`}>
            <div className="text-[13px] text-zinc-800 font-medium">{item.title}</div>
          </button>
          <div className="text-[11px] text-zinc-400 mt-0.5">
            {item.moves_count} move{item.moves_count === 1 ? "" : "s"}
            {(item.streams || []).length ? ` · ${item.streams.length} stream${item.streams.length === 1 ? "" : "s"}` : ""}
            {` · Owner: ${item.owner}`}
          </div>
          {overdue && <span className="inline-flex items-center gap-1 text-[10px] text-red-600 font-semibold mt-1"><AlertTriangle size={11} /> Target date passed</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <select data-testid={`gate-status-${item.id}`} className={`${inputCls} font-semibold ${statusCls(item.status)}`} value={item.status} disabled={busy}
            onChange={(e) => patch({ status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid={`gate-owner-${item.id}`} className={`${inputCls} w-28`} defaultValue={item.owner} disabled={busy}
            onBlur={(e) => e.target.value !== item.owner && patch({ owner: e.target.value })} title="Owner" />
          <input data-testid={`gate-target-${item.id}`} type="date" className={inputCls} defaultValue={item.target_date || ""} disabled={busy}
            onChange={(e) => patch({ target_date: e.target.value || null })} title="Target date" />
          <button data-testid={`gate-delete-${item.id}`} onClick={() => onDelete(item)} disabled={busy}
            className="text-zinc-300 hover:text-red-600 p-1" title={item.seed_ref ? "Hide (seeded)" : "Delete"}><Trash2 size={14} /></button>
        </div>
      </div>
      {expand && (
        <div data-testid={`gate-detail-${item.id}`} className="mt-2 ml-20 border-l-2 border-zinc-200 pl-3 space-y-1.5">
          {item.objective && <p className="text-[12px] text-zinc-600">{item.objective}</p>}
          {(item.moves || []).length > 0 && (
            <ul className="list-disc pl-4 text-[12px] text-zinc-600">{item.moves.slice(0, 20).map((m, i) => <li key={i}>{m}</li>)}</ul>
          )}
          {(item.updates || []).length > 0 && (
            <div className="pt-1 space-y-1">
              {item.updates.map((u, i) => (
                <div key={i} className="text-[11px] text-zinc-500">
                  <span className="text-zinc-400">{new Date(u.at).toLocaleDateString("en-GB")}</span>
                  {u.from_status && <> · {u.from_status} → <b>{u.status}</b></>}
                  {u.note && <> · {u.note}</>}{u.by && <span className="text-zinc-300"> · {u.by}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddGateForm({ onAdd, busy }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", objective: "", owner: "", target_date: "" });
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Enter a milestone title"); return; }
    await onAdd(f); setF({ title: "", objective: "", owner: "", target_date: "" }); setOpen(false);
  };
  if (!open) return (
    <button data-testid="gate-add-toggle" onClick={() => setOpen(true)}
      className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5">
      <Plus size={14} /> Add milestone
    </button>
  );
  return (
    <div data-testid="gate-add-form" className="border border-zinc-200 rounded-lg p-3 bg-zinc-50 space-y-2">
      <input data-testid="gate-add-title" className={`${inputCls} w-full`} placeholder="Milestone / gate title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <input data-testid="gate-add-objective" className={`${inputCls} w-full`} placeholder="Objective — what must be true to pass" value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} />
      <div className="grid sm:grid-cols-2 gap-2">
        <input data-testid="gate-add-owner" className={inputCls} placeholder="Owner" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} />
        <input data-testid="gate-add-target" type="date" className={inputCls} value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} title="Target date" />
      </div>
      <div className="flex gap-2">
        <button data-testid="gate-add-submit" onClick={submit} disabled={busy}
          className="text-[12px] tracking-[0.12em] uppercase bg-[var(--e360-primary)] text-white px-4 py-1.5 rounded disabled:opacity-40">Add</button>
        <button onClick={() => setOpen(false)} className="text-[12px] tracking-[0.12em] uppercase text-zinc-500 px-3 py-1.5">Cancel</button>
      </div>
    </div>
  );
}

const Stat = ({ label, value, cls = "text-black" }) => (
  <div className="text-center"><div className={`text-2xl font-semibold ${cls}`}>{value}</div>
    <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">{label}</div></div>
);

export default function StageGates({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/gates`); setData(data); }
    catch { toast.error("Could not load the stage-gate tracker"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const withBusy = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setData(data); if (ok) toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const sync = () => withBusy(() => api.post(`/admin/enterprise360/clients/${clientId}/gates/seed`), "Synced from blueprint");
  const add = (body) => withBusy(() => api.post(`/admin/enterprise360/clients/${clientId}/gates`, body), "Milestone added");
  const save = (id, body) => withBusy(() => api.put(`/admin/enterprise360/clients/${clientId}/gates/${id}`, body));
  const del = (item) => { if (!window.confirm(item.seed_ref ? "Hide this seeded gate?" : "Delete this milestone?")) return;
    withBusy(() => api.delete(`/admin/enterprise360/clients/${clientId}/gates/${item.id}`), "Removed"); };

  const s = data?.summary || {};
  const items = data?.items || [];
  const overallCls = s.overall === "red" ? "text-red-700 bg-red-50 border-red-200" : s.overall === "amber" ? "text-amber-700 bg-amber-50 border-amber-200" : "text-emerald-700 bg-emerald-50 border-emerald-200";
  return (
    <div data-testid="stage-gates">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-[12px] text-zinc-500 max-w-xl">The 30/60/90/180/360-day roadmap horizons as markable delivery gates — mark each on-track, at-risk, passed or missed.</p>
        <div className="flex items-center gap-2">
          {data?.seed_available > 0 && <span data-testid="gate-seed-available" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">{data.seed_available} new from blueprint</span>}
          <button data-testid="gate-sync-btn" onClick={sync} disabled={busy}
            className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5 disabled:opacity-40">
            <RefreshCw size={13} /> Sync from blueprint</button>
        </div>
      </div>
      <div data-testid="gate-summary" className="flex items-center gap-6 flex-wrap border border-zinc-200 rounded-lg px-4 py-3 mb-4">
        <span data-testid="gate-overall" className={`text-[12px] rounded-full px-3 py-1 border font-semibold ${overallCls}`}>
          <Flag size={11} className="inline mr-1" /> {s.overall === "green" ? "On plan" : s.overall === "amber" ? "Watch" : "Off track"}
        </span>
        <Stat label="Passed" value={s.passed ?? 0} cls="text-emerald-600" />
        <Stat label="At risk" value={s.at_risk ?? 0} cls={s.at_risk ? "text-amber-600" : "text-black"} />
        <Stat label="Overdue" value={s.overdue ?? 0} cls={s.overdue ? "text-red-600" : "text-black"} />
        <Stat label="Upcoming" value={s.upcoming ?? 0} />
        <Stat label="Missed" value={s.missed ?? 0} cls={s.missed ? "text-red-600" : "text-zinc-400"} />
      </div>
      <div className="mb-3"><AddGateForm onAdd={add} busy={busy} /></div>
      {loading ? <div className="text-sm text-zinc-400 py-6 text-center">Loading gates…</div>
        : items.length === 0 ? <div className="text-sm text-zinc-500 py-6 text-center">No gates yet. Sync from the blueprint or add a milestone.</div>
        : <div className="border border-zinc-200 rounded-lg divide-y" data-testid="gate-list">
            {items.map((it) => <GateRow key={it.id} item={it} onSave={save} onDelete={del} busy={busy} />)}
          </div>}
    </div>
  );
}
